/* eslint-disable react/prop-types */
import React, {
	useState, useEffect, useRef, useCallback
} from 'react';
import FocusTrap from 'focus-trap-react';
import classNames from 'classnames';

import { formatBoldDateRange } from 'utilities/date';
import { getCurrentPosition } from 'utilities/geolocation';
import { setPostalCode } from 'features/atp/api';
import { ERRORS, FALLBACK_CALLOUT_TEXT } from 'features/atp/config';
import { calculateCutoffTime } from 'features/atp/utilities';
import getZipCodeByLocation from 'features/regions/api/getZipCodeByGeoLocation';

import './AtpDeliveryInfo.scss';

type AtpDeliveryInfoProps = {
	zipCode: string;
	deliveryDate: string;
	deliveryDateLabel: string;
	cutoffDate?: string;
	showIcon?: boolean;
	fallbackCalloutText?: string,
	fallbackDateLabel?: string;
	shippingOptionLabel?: string;
	shippingOptionDisabled?: boolean;
	shippingOptionClassName?: string;
	showNoAtpAndFallbackError?: boolean;
	onZipChangeSuccess?: (zip: string, country: string) => Promise<void>;
};

const AtpDeliveryInfo: React.FC<AtpDeliveryInfoProps> = ({
	zipCode,
	deliveryDate,
	deliveryDateLabel,
	fallbackCalloutText = FALLBACK_CALLOUT_TEXT.SHIPS_IN,
	cutoffDate,
	showIcon,
	fallbackDateLabel,
	shippingOptionLabel,
	shippingOptionDisabled,
	shippingOptionClassName,
	showNoAtpAndFallbackError,
	onZipChangeSuccess
}) => {

	const localCutoffDate = calculateCutoffTime(cutoffDate);
	const [ currentZip, setCurrentZip ] = useState<string>(zipCode);
	const [ inputZip, setInputZip ] = useState<string>('');
	const [ showPopup, setShowPopup ] = useState<boolean>(false);
	const [ errorMsg, setErrorMsg ] = useState<string>('');
	const [ isUpdateBtnLoading, setIsUpdateBtnLoading ] = useState<boolean>(false);
	const [ isUseBtnLoading, setIsUseBtnLoading ] = useState<boolean>(false);
	const popupRef = useRef<HTMLDivElement>(null);
	const inputZipRef = useRef<HTMLInputElement>(null);
	const zipButtonRef = useRef<HTMLButtonElement>(null);
	const closeButtonRef = useRef<HTMLButtonElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	const [ positionAbove, setPositionAbove ] = useState<boolean>(false);
	const [ isClosingAbove, setIsClosingAbove ] = useState<boolean>(false);
	const [ isPopupHiding, setIsPopupHiding ] = useState<boolean>(true);

	const hasDateLabel = !!(deliveryDateLabel || fallbackDateLabel);
	const hasZip = !!currentZip;
	const hasNoAtpAndFallback = showNoAtpAndFallbackError && !hasDateLabel;

	const getTransitionDuration = useCallback(() => {

		if (containerRef.current) {

			const computedStyle = getComputedStyle(containerRef.current);
			const durationString = computedStyle.getPropertyValue('--atp-popup-transition-duration').trim();

			// Convert s to ms
			if (durationString.endsWith('s')) {

				return parseFloat(durationString) * 1000;

			}

			if (durationString.endsWith('ms')) {

				return parseFloat(durationString);

			}

		}
		return 200; // Fallback to a default value if unable to read (e.g., if ref not ready)

	}, []);

	const closePopup = useCallback(() => {

		if (popupRef.current) {

			if (positionAbove) {

				setIsClosingAbove(true);

			}

			setIsPopupHiding(true);

			const duration = getTransitionDuration();
			setTimeout(() => {

				setShowPopup(false);
				setPositionAbove(false);
				setIsClosingAbove(false);

				if (popupRef.current) {

					popupRef.current.style.left = '';

				}

			}, duration);

		} else {

			setShowPopup(false);
			setPositionAbove(false);
			setIsClosingAbove(false);

		}
		setErrorMsg('');

	}, [ positionAbove, getTransitionDuration ]);

	useEffect(() => {

		const handleClickOutside = (event: PointerEvent) => {

			if (showPopup && popupRef.current && !popupRef.current.contains(event.target as Node)
                && zipButtonRef.current && !zipButtonRef.current.contains(event.target as Node)
			) {

				closePopup();

			}

		};

		const handleKeyDown = (event: KeyboardEvent) => {

			if (event.key === 'Escape') {

				closePopup();

			}

		};

		if (showPopup) {

			document.addEventListener('mousedown', handleClickOutside);
			document.addEventListener('keydown', handleKeyDown);

		}

		return () => {

			document.removeEventListener('mousedown', handleClickOutside);
			document.removeEventListener('keydown', handleKeyDown);

		};

	}, [ showPopup, closePopup ]);

	useEffect(() => {

		if (showPopup) {

			if (!popupRef.current || !zipButtonRef.current || !containerRef.current) {

				return;

			}

			const zipBtn = zipButtonRef.current;
			const popup = popupRef.current;
			const container = containerRef.current;

			const btnRect = zipBtn.getBoundingClientRect();
			const containerRect = container.getBoundingClientRect();

			const popupHeight = popup.offsetHeight;
			const popupWidth = popup.offsetWidth;

			const windowHeight = window.innerHeight;
			const spaceBelow = windowHeight - btnRect.bottom;
			const spaceAbove = btnRect.top;

			const shouldFlip = spaceBelow < popupHeight && spaceAbove >= popupHeight;
			setPositionAbove(shouldFlip);
			setIsClosingAbove(false);

			const buttonCenter = btnRect.left + btnRect.width / 2;
			const left = buttonCenter - containerRect.left - popupWidth / 2;
			popup.style.left = `${left}px`;

			requestAnimationFrame(() => {

				setIsPopupHiding(false);

			});

		} else {

			setIsPopupHiding(true);

		}

	}, [ showPopup ]);

	// Override focusTrap's initial focus behavior of selecting the text on focus
	useEffect(() => {

		if (showPopup && inputZipRef.current) {

			requestAnimationFrame(() => {

				const inputElement = inputZipRef.current;
				inputElement.setSelectionRange(inputElement.value.length, inputElement.value.length);

			});

		}

	}, [ showPopup, inputZipRef ]);

	const handleTogglePopup = () => {

		if (showPopup) {

			return;

		}

		setInputZip(currentZip);
		setErrorMsg('');
		setShowPopup(true);

	};

	const updateZipCode = async (newZip: string) => {

		const postalCodeResponse = await setPostalCode(newZip);

		if (postalCodeResponse?.IsSuccess) {

			const outputZip = postalCodeResponse.ResponseModel.PostalCode;
			const countryCode = postalCodeResponse.ResponseModel.CountryCode;

			closePopup();

			if (onZipChangeSuccess) {

				// Component will be unmounted here and re-rendered with new data
				onZipChangeSuccess(outputZip, countryCode);

			} else {

				setCurrentZip(outputZip);

			}
			return Promise.resolve();

		}

		const error = postalCodeResponse?.ErrorMessage || ERRORS.POSTAL_CODE_UPDATE;
		setErrorMsg(error);

		inputZipRef.current?.select();
		inputZipRef.current?.focus();

		return Promise.reject(new Error(error));

	};

	const handleZipCodeUpdate = () => {

		const zip = inputZip.trim();
		setIsUpdateBtnLoading(true);
		updateZipCode(zip).finally(() => {

			setIsUpdateBtnLoading(false);

		});

	};

	const handleUseLocation = () => {

		setErrorMsg('');
		if ('geolocation' in navigator) {

			const geoSuccess = async (position: GeolocationPosition) => {

				const { latitude, longitude } = position.coords;
				const zip = await getZipCodeByLocation(latitude, longitude);

				if (zip) {

					setIsUseBtnLoading(true);
					updateZipCode(zip).finally(() => {

						setIsUseBtnLoading(false);

					});

				} else {

					setErrorMsg(ERRORS.POSTAL_CODE_NOT_FOUND);

				}

			};

			const geoError = (error: GeolocationPositionError) => {

				setErrorMsg(ERRORS.GEOLOCATION_ERROR);

				// eslint-disable-next-line no-console
				console.error('Error getting geolocation', error);

			};

			getCurrentPosition().then(geoSuccess).catch(geoError);

		} else {

			setErrorMsg(ERRORS.GEOLOCATION_NOT_SUPPORTED);

		}

	};

	const updateZipButtonClasses = classNames('AtpDeliveryInfo__updateZipBtn', 'calloutBtn', {
		loading: isUpdateBtnLoading
	});

	const useMyLocationButtonClasses = classNames('AtpDeliveryInfo__useLocationBtn', 'anchorLink', {
		loading: isUseBtnLoading
	});

	return (
		<div className="AtpDeliveryInfo" ref={containerRef}>
			<div className="AtpDeliveryInfo__inner">
				{showIcon && <span className="AtpDeliveryInfo__icon" role="img" aria-label="delivery truck icon" />}
				{' '}
				<span className="AtpDeliveryInfo__callout">
					{hasDateLabel && (
						<>
							<span className="AtpDeliveryInfo__calloutText">
								{deliveryDateLabel ? FALLBACK_CALLOUT_TEXT.GET_IT_BY : fallbackCalloutText}
							</span>
							{' '}
							<span
								className="AtpDeliveryInfo__date"
								data-date={deliveryDate}
								// eslint-disable-next-line react/no-danger
								dangerouslySetInnerHTML={{
									__html: formatBoldDateRange(deliveryDateLabel || fallbackDateLabel)
								}}
							/>
							{!hasZip && fallbackDateLabel ? '.' : ''}
						</>
					)}
					{shippingOptionLabel && hasZip && (
						<>
							{' '}
							{hasDateLabel && <span className="AtpDeliveryInfo__via">via</span>}
							{' '}
							<button
								type="button"
								className={shippingOptionClassName || ''}
								aria-haspopup="true"
								aria-disabled={shippingOptionDisabled ? 'true' : 'false'}
							>
								{shippingOptionLabel}
							</button>
						</>
					)}
					{hasNoAtpAndFallback && (
						<span className="AtpDeliveryInfo__errorMsg">
							Unable to get delivery dates
							{hasZip ? ' to ' : ''}
						</span>
					)}
					{hasZip && (hasDateLabel || hasNoAtpAndFallback) && (
						<>
							{!hasNoAtpAndFallback && (
								<>
									{' '}
									<span className="AtpDeliveryInfo__to">to</span>
									{' '}
								</>
							)}
							<button
								type="button"
								className="AtpDeliveryInfo__zipBtn anchorLink anchorLink--red"
								onClick={handleTogglePopup}
								aria-haspopup="true"
								aria-expanded={showPopup}
								ref={zipButtonRef}
							>
								{currentZip}
							</button>
						</>
					)}
					{!hasZip && (
						<>
							{hasDateLabel ? ' ' : ''}
							<button
								type="button"
								className="AtpDeliveryInfo__zipBtn anchorLink anchorLink--red"
								onClick={handleTogglePopup}
								aria-haspopup="true"
								aria-expanded={showPopup}
								ref={zipButtonRef}
							>
								Add Zip/Postal Code for Delivery Date
							</button>
						</>
					)}
					{hasDateLabel && localCutoffDate && (
						<span className="AtpDeliveryInfo__urgentCallout">
							{' '}
							<span className="AtpDeliveryInfo__ifOrderedIn">if ordered in</span>
							{' '}
							<span className="AtpDeliveryInfo__cutoff" data-cutoff-utc={cutoffDate}>
								{localCutoffDate}
							</span>
						</span>
					)}
				</span>
			</div>

			{showPopup && (
				<FocusTrap
					focusTrapOptions={{
						clickOutsideDeactivates: true,
						initialFocus: () => inputZipRef.current || closeButtonRef.current || false
					}}
				>
					<div
						className={classNames(
							'AtpDeliveryInfo__zipPopup',
							{ 'AtpDeliveryInfo__zipPopup--above': positionAbove || isClosingAbove },
							{ 'AtpDeliveryInfo__zipPopup--hiding': isPopupHiding }
						)}
						ref={popupRef}
					>
						<button
							type="button"
							className="AtpDeliveryInfo__closeBtn lpIcon-close02"
							onClick={closePopup}
							aria-label="Close popup"
							ref={closeButtonRef}
						/>
						<div className="AtpDeliveryInfo__popupTitle">Delivery Zip/Postal Code</div>
						<form
							className="AtpDeliveryInfo__zipForm"
							onSubmit={(e) => {

								e.preventDefault();
								handleZipCodeUpdate();

							}}
						>
							<input
								type="text"
								name="postalCode"
								className="AtpDeliveryInfo__zipInput"
								placeholder="Enter zip code"
								value={inputZip}
								onChange={(e) => setInputZip(e.target.value)}
								aria-label="Enter zip code"
								ref={inputZipRef}
							/>
							<button
								type="submit"
								onClick={(e) => {

									e.preventDefault();
									handleZipCodeUpdate();

								}}
								className={updateZipButtonClasses}
								aria-disabled={isUpdateBtnLoading}
							>
								Update
							</button>
						</form>
						<button
							type="button"
							className={useMyLocationButtonClasses}
							onClick={handleUseLocation}
							aria-disabled={isUseBtnLoading}
						>
							Use My Location
						</button>
						{errorMsg && (
							<div className="AtpDeliveryInfo__errorMsg">
								{errorMsg}
							</div>
						)}
					</div>
				</FocusTrap>
			)}
		</div>
	);

};

export default AtpDeliveryInfo;
